import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { FlightPostBookingSelectors } from "@b2bportal/core-flights";
import {
  ScheduleChangeUserActionEnum,
  type TimeGroupingEnum,
} from "@b2bportal/core-types";
import { SCHEDULE_CHANGE_DENY_CONTACT_AIRLINE_HOURS } from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import type { IFlightScheduleChangeFormProps } from "@tenant/flights";

export const useFlightScheduleChangeForm = (
  props: IFlightScheduleChangeFormProps
) => {
  const { t } = useI18nContext();
  const airports = useSelector(FlightPostBookingSelectors.getAirports);
  const [userAction, setUserAction] = useState(
    ScheduleChangeUserActionEnum.Accept
  );
  const [outboundTimes, setOutboundTimes] = useState<TimeGroupingEnum[]>([]);
  const [returnTimes, setReturnTimes] = useState<TimeGroupingEnum[]>([]);

  const onUserActionChange = useCallback(
    (action: ScheduleChangeUserActionEnum) => {
      setUserAction(action);

      if (action === ScheduleChangeUserActionEnum.Deny) {
        setOutboundTimes([]);
        setReturnTimes([]);
      }
    },
    []
  );

  return useMemo(() => {
    const { hoursToDeparture = 0, outboundChanged, returnChanged } = props;
    const hasAccepted = userAction === ScheduleChangeUserActionEnum.Accept;
    const hasRejected = userAction === ScheduleChangeUserActionEnum.Deny;
    const invalidDenyOutbound = outboundChanged && outboundTimes.length === 0;
    const invalidDenyReturn = returnChanged && returnTimes.length === 0;
    const continueDisabled =
      hasRejected && (invalidDenyOutbound || invalidDenyReturn);
    const contactAirlineOnReject =
      hoursToDeparture < SCHEDULE_CHANGE_DENY_CONTACT_AIRLINE_HOURS;
    const denyCopyContext = contactAirlineOnReject ? "contactAirline" : "";

    return {
      context: {
        airports,
        contactAirlineOnReject,
        continueDisabled,
        hasAccepted,
        hasRejected,
        invalidDenyOutbound,
        invalidDenyReturn,
        outboundTimes,
        returnTimes,
        userAction,
      },
      handlers: {
        onUserActionChange,
        setOutboundTimes,
        setReturnTimes,
      },
      text: {
        headingCopy: t(
          "core-flights.postbooking.scheduleChange.form.formTitle"
        ),
        acceptButtonTitle: t(
          "core-flights.postbooking.scheduleChange.form.acceptButtonTitle"
        ),
        acceptDetails: t(
          "core-flights.postbooking.scheduleChange.form.acceptDetails"
        ),
        denyButtonTitle: t(
          "core-flights.postbooking.scheduleChange.form.denyButtonTitle",
          {
            context: denyCopyContext,
          }
        ),
        denyDetails: t(
          "core-flights.postbooking.scheduleChange.form.denyDetails",
          {
            context: denyCopyContext,
          }
        ),
        outboundTimePreferenceCopy: t(
          "core-flights.postbooking.scheduleChange.form.timePreference",
          {
            flightType: "outbound",
          }
        ),
        returnTimePreferenceCopy: t(
          "core-flights.postbooking.scheduleChange.form.timePreference",
          {
            flightType: "return",
          }
        ),
        submitButtonLabel: t(
          "core-flights.postbooking.scheduleChange.form.submitButtonLabel"
        ),
      },
    };
  }, [
    airports,
    onUserActionChange,
    outboundTimes,
    props,
    returnTimes,
    t,
    userAction,
  ]);
};

export type IFlightScheduleChangeFormData = ReturnType<
  typeof useFlightScheduleChangeForm
>;
